import { useEffect, useState, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage } from "@/i18n";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Trattamenti from "@/pages/Trattamenti";
import Articoli from "@/pages/Articoli";
import ArticoloDettaglio from "@/pages/ArticoloDettaglio";
import Curriculum from "@/pages/Curriculum";
import Contatti from "@/pages/Contatti";
import Admin from "@/pages/Admin";
import SezioneDinamica from "@/pages/SezioneDinamica";
import Sezioni from "@/pages/Sezioni";
import Disdica from "@/pages/Disdica";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8 text-center">
          <p className="font-serif text-2xl text-slate-700">Si è verificato un errore. Ricarica la pagina.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function LenisSetup() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SeoManager() {
  const { pathname } = useLocation();
  const [hiddenPaths, setHiddenPaths] = useState(null);
  useEffect(() => {
    api
      .get("/seo/indexability")
      .then((res) => setHiddenPaths(res.data.paths || {}))
      .catch(() => setHiddenPaths({}));
  }, []);
  useEffect(() => {
    if (!hiddenPaths) return;
    const normalized = pathname.replace(/\/+$/, "") || "/";
    const isHidden = Object.keys(hiddenPaths).some(
      (p) => normalized === p || normalized.startsWith(p + "/")
    );
    let meta = document.querySelector('meta[name="robots"]');
    if (isHidden) {
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", "noindex, nofollow");
    } else if (meta && (meta.getAttribute("content") || "").includes("noindex")) {
      meta.remove();
    }
  }, [pathname, hiddenPaths]);
  return null;
}

function PageMeta() {
  const { pathname } = useLocation();
  const { lang, t } = useLanguage();
  const seo = t.seo || {};
  let key = "home";
  if (pathname.startsWith("/trattamenti")) key = "trattamenti";
  else if (pathname.startsWith("/articoli/")) key = null;
  else if (pathname.startsWith("/articoli")) key = "articoli";
  else if (pathname.startsWith("/curriculum")) key = "curriculum";
  else if (pathname.startsWith("/contatti")) key = "contatti";
  else if (pathname.startsWith("/sezioni")) key = "sezioni";
  const meta = key ? seo[key] : null;

  useEffect(() => {
    if (!meta) return;
    document.title = meta.title;
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", meta.desc);
    setMeta("og:title", meta.title, true);
    setMeta("og:description", meta.desc, true);
    setMeta("og:locale", lang === "it" ? "it_IT" : "en_US", true);
    setMeta("twitter:title", meta.title);
    setMeta("twitter:description", meta.desc);
  }, [meta, lang]);

  return null;
}

function Layout() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      <LenisSetup />
      <ScrollToTop />
      <SeoManager />
      <PageMeta />
      <Toaster richColors closeButton position="bottom-right" />
      {!isAdmin && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/trattamenti" element={<Trattamenti />} />
          <Route path="/articoli" element={<Articoli />} />
          <Route path="/articoli/:slug" element={<ArticoloDettaglio />} />
          <Route path="/curriculum" element={<Curriculum />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/sezioni" element={<Sezioni />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/sezione/:slug" element={<SezioneDinamica />} />
          <Route path="/disdica" element={<Disdica />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <Layout />
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
